export const isShippingRestrictedForParent = (shoot, parent) => {
  const country = parent?.address?.country || 'US';
  return shoot?.block_international_shipping === true && country !== 'US';
};

// Restriction rule depends on the product shape:
//
//   - YPX / pick-from-menu parent_product: the parent's own
//     `requires_shipping` is a coarse aggregate (often `true` even when a
//     digital-download child exists). The children's deliverability is
//     authoritative: as long as ONE non-shippable child exists, an
//     international parent can repeat-pick that child to fill the bundle's
//     slots, so the parent is allowed.
//
//   - Multi-config bundle (is_multi_config=true): every child is required
//     and not substitutable. Block if the parent itself requires shipping
//     OR any child does.
//
//   - Leaf product: use its own `requires_shipping`.
//
// Note: we detect a "YPX" as `product_type === 'parent_product' &&
// !is_multi_config`. `is_ypx` is the server's own derived flag that means
// the same thing.
export const isProductAllowed = (product, restricted) => {
  if (!restricted) return true;

  const children = Array.isArray(product?.child_products)
    ? product.child_products
    : [];
  const isParent =
    product?.product_type === 'parent_product' || product?.is_ypx;

  if (isParent && product?.is_multi_config) {
    if (product?.requires_shipping === true) return false;
    if (children.length === 0) return true;
    return !children.some((c) => c?.requires_shipping === true);
  }

  if (isParent && children.length > 0) {
    // YPX: children decide, parent's own requires_shipping is ignored.
    return children.some((c) => c?.requires_shipping !== true);
  }

  // Leaf / regular product.
  return product?.requires_shipping !== true;
};

// True when the international parent on a restricted shoot should see a
// YPX bundle but with shippable children hidden inside the configurator.
// Returns false for both fully-allowed and fully-blocked YPXes — only the
// in-between "mixed" case. Doesn't apply to multi_config (those just block).
// Matches the YPX semantics in `isProductAllowed`: the parent's own
// `requires_shipping` is ignored — children are authoritative.
export const ypxHasHiddenChildren = (product, restricted) => {
  if (!restricted) return false;
  if (product?.is_multi_config) return false;
  const children = Array.isArray(product?.child_products)
    ? product.child_products
    : [];
  if (children.length === 0) return false;
  const anyShippable = children.some((c) => c?.requires_shipping === true);
  const anyNonShippable = children.some((c) => c?.requires_shipping !== true);
  return anyShippable && anyNonShippable;
};

// A category is "restricted" when, for a non-US parent on a restricted shoot,
// every directly-orderable leaf is blocked. Walks `child_category_ids`
// recursively so a parent whose only content is restricted sub-categories
// also bubbles up (e.g. "Customize Your Package" whose sub-cards are all
// shippable YPX bundles).
//
// `product_type === 'child_product'` entries are dropped first: those are
// the YPX's individual options listed flat alongside the parent on the
// category, but they aren't directly orderable on their own — they're only
// reachable through the YPX configurator. Including them in the check would
// falsely mark a category Available just because one shipping-free YPX
// child happened to be enumerated at the top level.
export const isCategoryRestricted = (
  category,
  allCategories,
  restricted,
  visited = new Set(),
) => {
  if (!restricted) return false;
  const path = new Set(visited);

  // Cycle guard: a category that references itself (directly or transitively)
  // through child_category_ids shouldn't be re-entered. Keep the path local to
  // this branch so sibling sub-categories do not contaminate each other.
  if (category?.id != null) {
    if (path.has(category.id)) return true;
    path.add(category.id);
  }

  const products = (category?.products || []).filter(
    (p) => p?.product_type !== 'child_product',
  );
  const childCategoryIds = category?.child_category_ids || [];

  const childCategories =
    childCategoryIds.length > 0 && Array.isArray(allCategories)
      ? allCategories.filter((c) => childCategoryIds.includes(c.id))
      : [];

  const hasProducts = products.length > 0;
  const hasChildren = childCategories.length > 0;

  // No content at all — placeholder category, leave it visible / available.
  if (!hasProducts && !hasChildren) return false;

  const productsAllRestricted =
    !hasProducts || products.every((p) => !isProductAllowed(p, restricted));
  const childrenAllRestricted =
    !hasChildren ||
    childCategories.every((c) =>
      isCategoryRestricted(c, allCategories, restricted, new Set(path)),
    );

  return productsAllRestricted && childrenAllRestricted;
};

// Restriction-aware filter for incentive products. Drops two flavors of
// incentives a non-shippable parent can't use:
//   - `requires_shipping: true`: physical incentives (e.g. "Free Backpack").
//   - `is_free_shipping: true`: a shipping-discount incentive (the studio's
//     "Free Shipping" product), irrelevant when nothing in the order ships.
// Note `requires_shipping` is NOT authoritative for the "Free Shipping"
// product — that product is typically modeled with `requires_shipping: false`
// because the item itself isn't a physical thing being shipped, so the
// `is_free_shipping` flag is the load-bearing discriminator. When `restricted`
// is false this returns the original array reference unchanged — the caller
// does not need to special-case it.
export const availableIncentiveProducts = (incentiveProducts, restricted) => {
  if (!Array.isArray(incentiveProducts)) return [];
  if (!restricted) return incentiveProducts;
  return incentiveProducts.filter(
    (p) => p?.requires_shipping !== true && p?.is_free_shipping !== true,
  );
};
